import {Injectable} from '@angular/core';
import {EMPTY, Observable, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';

import {BannerOutlet} from './banner.component';

@Injectable({
  providedIn: 'root',
})
export class BannerService {
  private outlet?: BannerOutlet;
  private active?: Observable<number>;
  // The 500 ms pause before showing the next queued banner; the entry stays queued until it fires.
  private next?: ReturnType<typeof setTimeout>;
  private pending: { message: string, actions: string[], ret: Subject<number> }[] = [];
  // Ends the banners shown in an outlet when that outlet is replaced or destroyed: they can no longer be
  // answered, and a caller waiting for the answer (onceWhileShown) must learn that they are gone.
  private outletGone = new Subject<void>();

  // Use the supplied BannerOutlet to display messages. The newest outlet wins (VTK-3593): during a
  // route change or hot reload the next LoggedInComponent shell, and its outlet, is created before
  // the old one is destroyed, so throwing on a second outlet aborted the navigation.
  public init(val: BannerOutlet) {
    this.outlet = val;
    // A banner open in the replaced outlet can never be answered; don't let it block the queue.
    this.outletGone.next();
    this.active = undefined;
    this.cancelNext();
    this.showNext();
  }

  // Called when an outlet is destroyed; ignored unless it is still the current one. Queued banners
  // stay queued for the next outlet.
  public release(val: BannerOutlet) {
    if (this.outlet === val) {
      this.outletGone.next();
      this.outlet = undefined;
      this.active = undefined;
      this.cancelNext();
    }
  }

  // Display a message with at least one action. Returned observable will
  // emit the index of the selected action once the user clicks a button, and completes once the banner
  // is gone: answered, its outlet destroyed, or never shown because there is no outlet (e.g. /reports).
  public open(message: string, actions: string[]): Observable<number> {
    if (!this.outlet) {
      console.log('Tried to open banner but no outlet was defined.', message, actions);
      return EMPTY;
    }

    if (!this.active && !this.next && this.pending.length === 0) {
      return this.doOpen(message, actions);
    } else {
      const ret: Subject<number> = new Subject();
      this.pending.push({message, actions, ret});
      return ret.asObservable();
    }
  }

  // Actually show the banner in the outlet
  private doOpen(message: string, actions: string[]): Observable<number> {
    // Open the outlet and save the observable
    const active = this.outlet!.open(message, actions).pipe(take(1), takeUntil(this.outletGone));
    this.active = active;

    // When the user selects an action, the banner will close
    active.subscribe(() => {
      if (this.active !== active) {
        return; // answered in an outlet that has since been replaced
      }
      this.active = undefined;
      // If there was another queued, we show it
      if (this.pending.length > 0) {
        this.next = setTimeout(() => {
          this.next = undefined;
          this.showNext();
        }, 500);
      }
    });
    return active;
  }

  private showNext() {
    if (!this.outlet || this.active) {
      return;
    }
    const args = this.pending.shift();
    if (args) {
      this.doOpen(args.message, args.actions).subscribe(args.ret);
    }
  }

  private cancelNext() {
    clearTimeout(this.next);
    this.next = undefined;
  }
}
